import {Box, HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {DateTimeFormat} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {VehicleMotWarningResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

import {composePath, EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

import {VehicleMotCategory} from '../../../utils/getVehicleMotDataQuery';
import {VehicleMotRowLayout} from '../types/VehicleMotRowLayout';

interface VehicleMotWarningRowProps {
  vehicle: VehicleMotWarningResponseBody;
  category: VehicleMotCategory;
  layout: VehicleMotRowLayout;
  rowIndex: number;
  showBranch: boolean;
  isProbe: boolean;
}

// Fixed inline column widths keep the identifier, branch and date aligned across the rows of a
// section. A 17 character VIN is the widest identifier the preview can render and needs ~125px at
// the 12px `xSmall` size, so the column is 128px wide.
const INLINE_IDENTIFIER_WIDTH = 32;
const INLINE_BRANCH_WIDTH = 22;
const INLINE_VALID_UNTIL_WIDTH = 20;

export function VehicleMotWarningRow(props: VehicleMotWarningRowProps) {
  const hasRegistrationPlate = isNotNil(props.vehicle.registrationPlate);
  const identifier = props.vehicle.registrationPlate ?? props.vehicle.vin ?? EMPTY_PLACEHOLDER;
  const identifierLabel = hasRegistrationPlate
    ? i18n.t('entity.vehicle.labels.licensePlate')
    : i18n.t('entity.vehicle.labels.vin');
  const vehicleDetailUrl = composePath(vehiclesRoutes.detail, {
    params: {id: props.vehicle.id},
  });
  const validUntil = (
    <DateTimeFormat
      format="dateShort"
      value={parseDate(props.vehicle.technicalInspectionValidUntil)}
    />
  );

  return (
    <div
      data-vehicle-warehouse-row-layout={props.layout}
      data-vehicle-warehouse-visible-row={props.isProbe ? undefined : props.category}
      data-vehicle-warehouse-visible-row-index={props.isProbe ? undefined : props.rowIndex}
    >
      {match(props.layout)
        .with('inline', () => (
          // The row carries no vertical padding: the section spaces the rows and their separators
          // instead, which keeps a fifth row per column inside the wide tile's height budget.
          <HStack spacing={2} align="center" wrap="nowrap">
            <Box flex={1} minWidth={0}>
              <Link
                href={vehicleDetailUrl}
                title={props.vehicle.title ?? EMPTY_PLACEHOLDER}
                size="small"
                noWrap
              />
            </Box>
            <Box width={INLINE_IDENTIFIER_WIDTH} flexShrink={0}>
              <Text size="xSmall" color="secondary" noWrap>
                {identifier}
              </Text>
            </Box>
            <Show when={props.showBranch}>
              <Box width={INLINE_BRANCH_WIDTH} flexShrink={0}>
                <Text size="xSmall" color="secondary" noWrap>
                  {props.vehicle.branchName}
                </Text>
              </Box>
            </Show>
            <Box width={INLINE_VALID_UNTIL_WIDTH} flexShrink={0}>
              <Text size="xSmall" color="secondary" align="right" noWrap>
                {validUntil}
              </Text>
            </Box>
          </HStack>
        ))
        .with('stacked', () => (
          <VStack spacing={1}>
            <Link
              href={vehicleDetailUrl}
              title={props.vehicle.title ?? EMPTY_PLACEHOLDER}
              size="small"
              noWrap
            />
            <HStack spacing={2} wrap>
              <Text size="xSmall" color="secondary">
                {`${identifierLabel}: ${identifier}`}
              </Text>
              <Text size="xSmall" color="secondary">
                {`${i18n.t('general.labels.validUntil')}: `}
                {validUntil}
              </Text>
              <Show when={props.showBranch}>
                <Text size="xSmall" color="secondary">
                  {`${i18n.t('entity.branch.labels.branch')}: ${props.vehicle.branchName}`}
                </Text>
              </Show>
            </HStack>
          </VStack>
        ))
        .exhaustive()}
    </div>
  );
}
